// components/StatsSection.tsx

import React, { useContext, useEffect, useState } from "react";
import { ButtonType } from "components/atoms/Button/types";
import { Loader } from "components/atoms/Loader";
import MonsterStats from "components/atoms/MonsterStats";
import NPCStats from "components/atoms/NPCStats";
import CreateStatsForm from "components/molecules/CreateStatsForm";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import { deleteEntityById, getMonsterStatsByImageId, getNPCStatsByImageId, getNpcStatus, getStatus } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, DnD5eMonsterDTO, DnD5eNPCDTO, GameEntityDTO, ImageDTO, RequestType, UserPremiumType } from "types";
import { LOADING_TEXT } from "utils/promptConstants";
import { SubTitle } from "utils/styling/typography.styled";
import * as Styled from "./StatsSection.styled";
interface StatsSectionProps {
  image: ImageDTO;
  statsProps: GameEntityDTO | null;
}
const StatsSection: React.FC<StatsSectionProps> = ({
  image,
  statsProps
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [stats, setStats] = useState<GameEntityDTO | null>(statsProps);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false); // Add loading state

  useEffect(() => {
    setStats(statsProps);
  }, [statsProps]);
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (taskId && image?.id) {
      setLoading(true);
      intervalId = setInterval(async () => {
        try {
          let isCompleted = false;
          if (image.imageMetadata?.requestType === RequestType.MONSTER) {
            isCompleted = (await getStatus(taskId)) || false;
          } else if (image.imageMetadata?.requestType === RequestType.NPC) {
            isCompleted = (await getNpcStatus(taskId)) ?? false;
          }
          if (isCompleted) {
            let response;
            if (image.imageMetadata?.requestType === RequestType.MONSTER) {
              response = await getMonsterStatsByImageId(image.id);
            } else if (image.imageMetadata?.requestType === RequestType.NPC) {
              response = await getNPCStatsByImageId(image.id);
            }
            if (response) {
              setStats(response);
              setTaskId(null);
              setLoading(false);
              setShowForm(false);
              dispatch({
                type: AppReducerActionType.SET_GOLD,
                payload: {
                  ...state,
                  gold: state.gold - 4
                }
              });
              if (intervalId) clearInterval(intervalId);
            } else {
              setError("Failed to fetch stats");
              setLoading(false);
              if (intervalId) clearInterval(intervalId);
            }
          }
        } catch (e) {
          setError("Failed to fetch stats");
          setLoading(false);
          if (intervalId) clearInterval(intervalId);
        }
      }, 2000); // Poll every 2 seconds
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [taskId, image?.id, state, dispatch, image?.imageMetadata?.requestType]);
  const handleTaskCreated = (taskId: string) => {
    setTaskId(taskId);
    setLoading(true);
    setShowForm(false);
  };
  const handleDeleteStats = async () => {
    if (stats && stats.id) {
      try {
        await deleteEntityById(stats.entityType.toLowerCase(), stats.id);
        setStats(null);
        setShowRemoveConfirmModal(false);
      } catch (error) {
        setError("Failed to delete stats");
      }
    }
  };
  const renderStatsComponent = () => {
    if (!stats) return null;
    switch (image.imageMetadata.requestType) {
      case RequestType.MONSTER:
        return <MonsterStats stats={stats as DnD5eMonsterDTO} />;
      case RequestType.NPC:
        return <NPCStats stats={stats as DnD5eNPCDTO} />;
      default:
        return <div>Coming soon...</div>;
    }
  };
  const handleModalClose = () => {
    setShowRemoveConfirmModal(false);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const handleCreateStats = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (state.gold < 4) {
      dispatch({
        type: AppReducerActionType.SHOW_PRICING_TABLE,
        payload: {
          ...state,
          showPricingTable: true
        }
      });
      return;
    }
    setShowForm(true);
  };
  if (!image || !image.imageMetadata) {
    return <div>Loading...</div>;
  }
  const isUserImage = image.imageMetadata.username === state.username;
  const isSubscribed = state.userPremiumLevel !== UserPremiumType.NONE;
  if (error) {
    return <div>Error: {error}</div>;
  }
  if (loading) {
    return <Loader prompts={LOADING_TEXT} />;
  }
  if (!stats && !showForm && image.imageMetadata.requestType !== RequestType.MONSTER && image.imageMetadata.requestType !== RequestType.NPC) {
    return <Styled.StatsContainer>
                <Styled.HeaderContainer>
                    <SubTitle>Coming Soon...</SubTitle>
                    <Styled.InfoText>
                        Stats for this type of image are currently only
                        available for Monsters and NPCs but check back soon!
                    </Styled.InfoText>
                </Styled.HeaderContainer>
            </Styled.StatsContainer>;
  }
  if (!stats && !showForm && !loading) {
    return <Styled.StatsContainer>
                <Styled.HeaderContainer>
                    <SubTitle>No Stats Available</SubTitle>
                    <>
                        {isSubscribed ? <Styled.InfoText>
                                Generating stats costs 4 Gold.
                            </Styled.InfoText> : <>
                                <Styled.InfoText>
                                    You must be a Subscriber to generate stats.
                                </Styled.InfoText>
                                <Styled.InfoText>
                                    Generating stats costs 4 Gold.
                                </Styled.InfoText>
                            </>}
                        {state.isLoggedIn && isUserImage && <Styled.Button variant={ButtonType.SUCCESS} onClick={handleCreateStats}>
                                Create Stats
                            </Styled.Button>}
                    </>
                </Styled.HeaderContainer>
            </Styled.StatsContainer>;
  }
  if (!stats && showForm && !loading) {
    return <CreateStatsForm imageId={image?.id} onTaskCreated={handleTaskCreated} requestType={image?.imageMetadata.requestType} />;
  }
  const toRegularCase = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };
  return <Styled.StatsContainer data-sentry-element="unknown" data-sentry-component="StatsSection" data-sentry-source-file="index.tsx">
            <Styled.HeaderContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <SubTitle data-sentry-element="SubTitle" data-sentry-source-file="index.tsx">{`${toRegularCase(image?.imageMetadata.requestType)} Stats`}</SubTitle>
                {isUserImage && <Styled.RemoveButton onClick={() => setShowRemoveConfirmModal(true)}>
                        X
                    </Styled.RemoveButton>}
            </Styled.HeaderContainer>
            {renderStatsComponent()}
            {showRemoveConfirmModal && <DeleteConfirmationModal isOpen={showRemoveConfirmModal} onConfirm={handleDeleteStats} onCancel={handleModalClose} text="entry" />}
        </Styled.StatsContainer>;
};
export default StatsSection;